import { getSessionRealmRoles } from '@/session/token';

const getRealmRoles = (): string[] | undefined => {
  const roles = getSessionRealmRoles();
  return roles.length > 0 ? [...roles] : undefined;
};

// Exact-match membership over the committed realm-role set. This is the primitive every
// role gate should use: the session snapshot is sorted (src/session/token.ts), so any check
// that resolves the user down to a single role and compares is order-dependent and wrong.
export const hasRealmRole = (required: string | string[]): boolean => {
  const realmRoles = getRealmRoles();
  if (!realmRoles) {
    return false;
  }
  return (Array.isArray(required) ? required : [required]).some((role) => realmRoles.includes(role));
};

// Check if the user has the 'superuser' or 'acenda_the_management' role in their realm roles
export const isAcendaSuperOrManagement = (): boolean => {
  const realmRoles = getRealmRoles();
  if (!realmRoles) {
    return false;
  }
  return realmRoles.includes('superuser') || realmRoles.includes('acenda_the_management');
};

// same as isAcendaSuperOrManagement but also returns true for the 'playwright_test_user' role, so
// e2e tests can reach surfaces gated to super users / acenda management that the test user lacks.
// No production caller today: the settlement surfaces it used to gate now read canAccessSettlements
// (src/utils/settlementsAccess.ts), which deliberately omits playwright_test_user so its specs can
// drive both the granted and the denied path. Kept as the documented convention for future gates.
export const isAcendaSuperOrManagementOrPlaywrightTestUser = (): boolean => {
  if (isAcendaSuperOrManagement()) {
    return true;
  }

  const realmRoles = getRealmRoles();
  if (!realmRoles) {
    return false;
  }
  return realmRoles.includes('playwright_test_user');
};

// same as isAcendaStaff but also returns true if the user has the 'playwright_test_user' role
// which will be used to allow access to pages that require a role our test user does not have
export const isAcendaStaffOrPlaywrightTestUser = (): boolean => {
  if (isAcendaStaff()) {
    return true;
  }

  const realmRoles = getRealmRoles();
  if (!realmRoles) {
    return false;
  }
  return realmRoles.includes('playwright_test_user');
};

// Returns true if the user has any acenda staff role
export const isAcendaStaff = (): boolean => {
  const realmRoles = getRealmRoles();
  if (!realmRoles) {
    return false;
  }
  if (realmRoles.includes('superuser')) {
    return true;
  }

  // return true if any realm roles have the word 'acenda_' in them
  return realmRoles.some((role) => role.includes('acenda_'));
};

// Returns which acenda staff role the user has or false if none. Resolves by rank, not by
// array position: the realm-role snapshot is sorted, so a positional `.find()` would report
// e.g. 'acenda_support' for a user who also holds 'acenda_the_management'. Use hasRealmRole
// for access decisions - this single-role answer exists for display and superuser checks.
export const acendaStaffRole = (): string | false => {
  const realmRoles = getRealmRoles();
  if (!realmRoles) {
    return false;
  }
  if (realmRoles.includes('superuser')) {
    return 'superuser';
  }
  if (realmRoles.includes('acenda_the_management')) {
    return 'acenda_the_management';
  }

  return realmRoles.find((role) => role.includes('acenda_')) || false;
};

// Returns which acenda staff role the user has or false if none
export const acendaStaffRolePretty = (): string | false => {
  const role = acendaStaffRole();
  if (!role) {
    return false;
  }
  if (role === 'superuser') {
    return 'Super User';
  }
  return role
    .replace('acenda_', '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l: string) => l.toUpperCase());
};
